'use client'

import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { toE164 } from '@/utils/phone'

interface DangerousConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  warningTitle: string
  warningMessage: string
  confirmationLabel: string
  confirmationPlaceholder: string
  confirmationValue: string
  onConfirmationChange: (value: string) => void
  expectedValue: string
  mismatchError: string
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
  loadingText?: string
  /** If true, normalizes user input to E.164 format before comparing with expectedValue */
  normalizePhone?: boolean
  /** Default region for phone normalization (default: 'US') */
  phoneRegion?: string
}

/**
 * DangerousConfirmationDialog
 *
 * A dialog for dangerous/destructive actions that requires user confirmation
 * by typing a specific value (e.g., phone number, email, account name)
 *
 * Features:
 * - Warning message with icon
 * - Input field for confirmation
 * - Validates input matches expected value
 * - Optional phone number normalization (converts user-friendly format to E.164)
 * - Loading state during action
 * - Cannot be dismissed by clicking outside (must use Cancel, X, or Escape)
 * - Cannot be closed while loading
 */
export default function DangerousConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  warningTitle,
  warningMessage,
  confirmationLabel,
  confirmationPlaceholder,
  confirmationValue,
  onConfirmationChange,
  expectedValue,
  mismatchError,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  loadingText = 'Processing...',
  normalizePhone = false,
  phoneRegion = 'US',
}: DangerousConfirmationDialogProps) {
  // Normalize user input if phone normalization is enabled
  const normalizedValue = normalizePhone
    ? toE164(confirmationValue, phoneRegion) || ''
    : confirmationValue

  const isValid = normalizedValue === expectedValue

  // Handle dialog open/close state changes
  // This is called when X button is clicked, Escape is pressed, etc.
  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        // Prevent closing by clicking outside (for dangerous actions)
        onPointerDownOutside={(e) => e.preventDefault()}
        // Prevent Escape while loading
        onEscapeKeyDown={(e) => isLoading && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-destructive">{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4">
              {/* Warning Box */}
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div className="text-sm text-foreground">
                  <p className="font-semibold text-destructive mb-1">
                    {warningTitle}
                  </p>
                  <p>{warningMessage}</p>
                </div>
              </div>

              {/* Confirmation Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {confirmationLabel}
                </label>
                <Input
                  type="text"
                  value={confirmationValue}
                  onChange={(e) => onConfirmationChange(e.target.value)}
                  placeholder={confirmationPlaceholder}
                  disabled={isLoading}
                  autoComplete="off"
                  className="focus:ring-destructive focus:border-destructive"
                />
                {confirmationValue && !isValid && (
                  <p className="text-sm text-destructive mt-2">
                    {mismatchError}
                  </p>
                )}
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="touch-manipulation"
          >
            {cancelText}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading || !isValid}
            className="touch-manipulation gap-2"
          >
            {isLoading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {isLoading ? loadingText : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
